/**
 * Jobs service
 *
 * @version
 * 4.63.0 DEVOPS-715 order-admin-ui: setup new repo without vertical slicing
 * 4.61.0 OAUI-643 Add material and gauge fields for adjustments
 * 4.58.0 OAUI-637 Add phase code and label to order details
 * 4.37.0 OAUI-532 Add missing filters for v2 orders list
 * 4.36.0 OAUI-513 Order details: edit job bugs
 * 4.36.0 OAUI-507 Edit order dates
 * 4.36.0 OAUI-506 Edit order job
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2022-2023 Build Centrix
 */
import { Inject, Injectable } from '@angular/core';
import { WdGraphQlService } from '../graphql/wd-graphql.service';
import { defer, from, Observable, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class WdJobService {
  /**
   * Constructor
   * @since 4.36.0
   */
  constructor(
    private wdGraphQlService: WdGraphQlService,
    @Inject('wdErrorService') public wdErrorService: any,
    @Inject('wdOauth2HttpService') public wdOauth2HttpService: any,
    @Inject('wdSharedFunctionsService') public wdSharedFunctionsService: any,
  ) {}

  // Booleans
  searchingJobs = false;
  searchingJobsFailed = false;

  /**
   * Search Jobs Formatter
   * @since 4.37.0
   */
  typeaheadSearchJobsFormatter = (result: any): string =>
    result.number + ' ' + result.name;

  /**
   * Search Jobs
   * @since 4.37.0
   */
  typeaheadSearchJobs = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      tap(() => (this.searchingJobs = true)),
      switchMap((term) =>
        this.typeaheadSearchJobsApi(term).pipe(
          tap(() => (this.searchingJobsFailed = false)),
          catchError(() => {
            this.searchingJobsFailed = true;
            return of([]);
          }),
        ),
      ),
      tap(() => (this.searchingJobs = false)),
    );

  /**
   * Search Jobs API
   * @since 4.37.0
   */
  typeaheadSearchJobsApi(term: string) {
    if (term === '') {
      return of([]);
    }
    return defer(() => from(this.searchJobs(term))).pipe(
      map((response: any) => {
        return response.slice(0, 10); // Return 10 results max
      }),
    );
  }

  /**
   * Get Phases by Job Index
   *
   * @since 4.36.0
   * @memberof WdJobService
   */
  async getPhasesByIndex(jobIndex: string): Promise<Array<object>> {
    // Phases
    let phases: Array<any> = [];

    try {
      // Set Query
      const query = {
        query:
          'query{ jobPhases(jobId:"' + jobIndex + '") { id, code, label } }',
      };
      // Get Phases
      const response = await this.wdGraphQlService.fetchGraphQlData(query);
      // Set Phases
      if (response.jobPhases) {
        phases = response.jobPhases.map((item: any) => {
          item.labelWithCode = item.code + ' - ' + item.label; // The labelWithCode field is used to display the "phase code" and "phase label"
          return item;
        });
      }
      return phases;
    } catch (error) {
      // Output error
      this.wdErrorService.userError('Error', 'Failed to load job phases');
      return phases;
    }
  }

  /**
   * Search Jobs from the API
   *
   * @since 4.36.0
   * @memberof WdJobService
   */
  async searchJobs(searchTerm: string): Promise<Array<object>> {
    // Jobs
    let jobs: Array<any> = [];
    // Start Loader
    this.searchingJobs = true;
    try {
      if (searchTerm) {
        // Set Query
        const query = {
          query:
            'query{ jobs(search:"' +
            searchTerm +
            '") { index, name, number } }',
        };
        // Get Jobs
        const response = await this.wdGraphQlService.fetchGraphQlData(query);
        // Set Jobs
        if (response.jobs) {
          jobs = response.jobs;
        }
      }
      return jobs;
    } catch (error) {
      // Stop Loader
      this.searchingJobs = false;
      // Output error
      this.wdErrorService.userError('Error', 'Failed to load jobs');
      return jobs;
    }
  }
}
